import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { kml } from '@tmcw/togeojson';
import { area } from '@turf/area';
import { booleanContains } from '@turf/boolean-contains';
import { booleanDisjoint } from '@turf/boolean-disjoint';
import { booleanIntersects } from '@turf/boolean-intersects';
import { booleanPointInPolygon } from '@turf/boolean-point-in-polygon';
import { booleanWithin } from '@turf/boolean-within';
import { centerOfMass } from '@turf/center-of-mass';
import { point, polygon } from '@turf/helpers';
import { kinks } from '@turf/kinks';
import { pointOnFeature } from '@turf/point-on-feature';
import { dsDtos } from '@vectronic/openApi-admin';
import { SnackbarService } from '@vectronic/util';
import { cloneDeep, isEqual } from 'es-toolkit';
import { FenceRules, Point, Polygon } from '../../types/leaflet-map-types';
import { L } from '../leaflet-setup/leaflet-setup';
import { MapService } from '../map/map.service';
import { DrawingManagerService } from './drawing-manager.service';

@Injectable({ providedIn: 'root' })
export class VirtualFenceService {
  private mapService = inject(MapService);
  private snackbarService = inject(SnackbarService);
  private drawingManagerService = inject(DrawingManagerService);

  allPolygons = signal<Polygon[]>([]);
  isEditMode = signal(false);
  readonly = signal(false);
  currentPointTypeEditMode: boolean;

  map: L.Map;

  drawHandler = signal<any>(null);
  currentDrawnLayer = signal<L.Polygon>(null);
  isEmptyVirtualFence = signal(false);
  private drawingCanvasLayer: L.Canvas | null = null;
  hasEmptyVirtualFence = signal(false);
  initialPolygons = signal<Polygon[]>([]);

  constructionRules = computed(() => this.prepareVirtualFenceRules(this.allPolygons()));

  isVirtualFenceFilled = computed(
    () =>
      this.isEmptyVirtualFence() ||
      (this.allPolygons()?.length && !isEqual(this.allPolygons(), this.initialPolygons())),
  );

  isVirtualFenceChanged = computed(
    () => !isEqual(this.allPolygons(), this.initialPolygons()) || this.isEmptyVirtualFence(),
  );
  isVirtualFenceChanged$ = toObservable(this.isVirtualFenceChanged);

  currentlyEditingFenceId: string | null = null;
  drawOptions = {
    position: 'topright',
    draw: {
      polyline: false,
      polygon: { allowIntersection: false },
      rectangle: false,
      circle: false,
      marker: false,
      circleMarker: false,
    },
    edit: { featureGroup: new L.FeatureGroup() },
  };

  setMap(map: L.Map) {
    this.map = map;
    setTimeout(() => {
      this.showSidebar();
      this.hideCircularMarker();
      this.displayFetchedPolygons(this.allPolygons(), true);
    }, 100);
  }

  initialize(
    virtualFenceConstructionRules: dsDtos.VirtualFenceConstructionRule[],
    isFilled: boolean,
  ) {
    const initialPolygons = this.dataAsPolygonStructure([
      {
        virtualFenceConstructionRules: virtualFenceConstructionRules,
      },
    ]);
    this.allPolygons.set(initialPolygons);
    if (!isFilled) {
      this.initialPolygons.set(initialPolygons);
    }
  }

  showSidebar() {
    const classList = document.querySelector('.leaflet-top.leaflet-left')?.classList;
    classList?.add('sidebar-shown');
    classList?.remove('sidebar-hidden');
  }

  destroyMap() {
    this.map = undefined;
  }

  createNewPolygon() {
    this.drawingManagerService.setActiveDrawingMode('virtual-fence');

    this.drawingCanvasLayer = L.canvas();
    this.map.addLayer(this.drawingCanvasLayer);

    L.drawLocal.draw.handlers.polygon.tooltip.start = 'drawing a shape';

    this.drawHandler.set(
      new L.Draw.Polygon(this.map as any, {
        ...this.drawOptions.draw.polygon,
        shapeOptions: {
          fillColor: 'transparent',
          fillOpacity: 0,
          renderer: this.drawingCanvasLayer,
        } as any,
      }),
    );
    this.drawHandler().enable();

    this.map?.once(L.Draw.Event.CREATED, (event: { layer: L.Layer }) => {
      this.map?.off(L.Draw.Event.DRAWSTOP, this.resetPolygonHandlers, this);
      const { layer } = event;
      this.mapService.layers.polygons.addLayer(layer);
      if (layer instanceof L.Polygon) {
        const polygonLatLngs = layer.getLatLngs()[0] as L.LatLng[];
        const rules = this.getFenceRules(polygonLatLngs);
        if (!rules) {
          this.markRedAndRemove(layer);
          this.snackbarService.error('invalid fence.');
          return;
        }
        const isNotValid = this.checkConditionsNewPolygon(rules);
        if (isNotValid) {
          this.markRedAndRemove(layer);
          this.snackbarService.error('invalid fence.');
        } else {
          this.beforeNewPolygonCreation(layer, polygonLatLngs);
        }
      }
      this.resetPolygonHandlers();
    });

    // This handles cancellation (e.g., pressing ESC).
    this.map?.once(L.Draw.Event.DRAWSTOP, this.resetPolygonHandlers, this);
  }

  private getInsideOutsidePoint(pointType: boolean, polygonLatLngs: L.LatLng[]) {
    if (!pointType) {
      const latlng = new L.LatLng(-52.62889532163714, -142.14056964483612);
      return { latlng, isInside: pointType };
    }

    const coordinates = polygonLatLngs.map(({ lat, lng }) => [lng, lat]);
    if (
      coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
      coordinates[0][1] !== coordinates[coordinates.length - 1][1]
    ) {
      coordinates.push(coordinates[0]);
    }
    const turfPolygon = polygon([coordinates]);
    const cOfMass = centerOfMass(turfPolygon);
    const isInside = booleanPointInPolygon(cOfMass, turfPolygon);
    let latlng: L.LatLng;
    if (!isInside) {
      const alternativePoint = pointOnFeature(turfPolygon);
      const [lng, lat] = alternativePoint.geometry.coordinates;
      latlng = new L.LatLng(lat, lng);
    } else {
      const [lng, lat] = cOfMass.geometry.coordinates;
      latlng = new L.LatLng(lat, lng);
    }
    return { latlng, isInside: pointType };
  }

  beforeNewPolygonCreation(layer: L.Polygon, polygonLatLngs: L.LatLng[], fenceName?: string) {
    this.currentDrawnLayer.set(layer);
    this.map?.addLayer(this.mapService.layers.polygons);

    let point = this.getInsideOutsidePoint(true, polygonLatLngs);
    const rules = this.getFenceRules(polygonLatLngs);
    if (
      rules.parentFence?.isCreated &&
      rules.parentFence?.childFence?.isPointInside &&
      point.isInside &&
      !this.hasFenceWithOutSidePoint()
    ) {
      point = this.getInsideOutsidePoint(false, polygonLatLngs);
      this.snackbarService.error('only outside area can be selected');
    }
    this.createPolygon(layer, point, polygonLatLngs, false, fenceName);
  }

  private createPolygon(
    polygonLayer: L.Polygon,
    point: Point,
    polygonLatLngs: L.LatLng[],
    isEditing = false,
    fenceName?: string,
  ) {
    const currentFenceId = isEditing ? (polygonLayer as any).customId : null;
    const rules = this.getFenceRules(polygonLatLngs);
    const isValid = this.checkConditions(rules, point, isEditing, currentFenceId);

    // if rules are not true
    if (!isValid) return;

    if (isEditing) {
      // remove if exists
      this.removeBlueOverlayLayer({
        lat: currentFenceId.split('|')[0],
        lng: currentFenceId.split('|')[1],
      });

      const updatedFences = this.allPolygons().map((fence) => {
        if (
          fence.insidePoint.lat == (polygonLayer as any).customId.split('|')[0] &&
          fence.insidePoint.lng == (polygonLayer as any).customId.split('|')[1]
        ) {
          return {
            ...fence,
            isPointInside: point.isInside,
            insidePoint: { lat: point.latlng.lat, lng: point.latlng.lng },
            fencePoints: polygonLatLngs.map((latlng) => ({
              lat: latlng.lat,
              lng: latlng.lng,
            })),
          };
        }
        return fence;
      });

      this.allPolygons.set(updatedFences);
      (polygonLayer as any).customId = `${point.latlng.lat}|${point.latlng.lng}`;
    } else {
      const newPolygon = {
        name: fenceName ?? 'Fence ' + (this.allPolygons().length + 1),
        isPointInside: point.isInside,
        insidePoint: { lat: point.latlng.lat, lng: point.latlng.lng },
        fencePoints: polygonLatLngs.map((latlng) => ({
          lat: latlng.lat,
          lng: latlng.lng,
        })),
      };

      const updatedFences = [...this.allPolygons(), newPolygon];
      this.allPolygons.set(updatedFences);
      this.isEmptyVirtualFence.set(false);
      (polygonLayer as any).customId = `${point.latlng.lat}|${point.latlng.lng}`;

      this.displayFencePopup(polygonLayer, newPolygon.name);
    }

    // Set styling based on point location
    this.applyColorToMap(point, polygonLayer);

    (polygonLayer as any).editing.disable();

    this.resetPolygonHandlers();
  }

  private applyColorToMap(point: Point, polygonLayer: L.Polygon) {
    if (point.isInside) {
      polygonLayer.setStyle({
        fillColor: '#1a79ff',
        fillOpacity: 0.2,
        pane: 'virtualFencePane',
      });
    } else {
      this.applyOutsideBlueOverlay(polygonLayer);
    }
  }

  private checkConditions(
    rules: FenceRules,
    point: Point,
    isEditing: boolean,
    currentFenceId?: string,
  ) {
    const conditions = [
      {
        check: rules.isFenceSelfIntersecting,
        message: 'Self-intersecting fences are not allowed.',
      },
      {
        check:
          this.hasFenceWithOutSidePoint(isEditing ? currentFenceId : '') &&
          (!point.isInside ||
            (rules.isAdjacentFence && rules.childFence?.parentFence?.isPointInside)),
        message:
          "A fence defining an 'outside area' already exists. Only one such fence is permitted.",
      },
      {
        check: !point.isInside && rules.isAdjacentFence,
        message: 'Point is invalid, only possible within the fence.1.',
      },
      {
        check:
          rules.parentFence?.isCreated &&
          rules.parentFence?.childFence?.isPointInside &&
          point.isInside &&
          isEditing,
        message: 'Point is invalid, only possible outside the fence.2.',
      },
      {
        check:
          !rules.parentFence?.isCreated &&
          !rules.childFence?.isCreated &&
          !rules.isPartialFence &&
          this.hasFenceWithOutSidePoint(isEditing ? currentFenceId : '') &&
          isEditing,
        message:
          "Disjointed fences are not allowed when a fence with an 'outside area' definition exists. Ensure new/edited fences overlap.",
      },
    ];

    for (const condition of conditions) {
      if (condition.check) {
        this.snackbarService.error(condition.message);
        return false;
      }
    }
    return true;
  }

  checkConditionsNewPolygon({
    parentFence,
    childFence,
    isPartialFence,
    isFenceSelfIntersecting,
  }: FenceRules) {
    const firstCheck = this.hasFenceWithOutSidePoint() && !childFence?.isCreated && !isPartialFence;
    const secondCheck = childFence?.isCreated && childFence?.parentFence?.isPointInside;
    const thirdCheck = parentFence?.isCreated && !parentFence?.childFence?.isPointInside;
    const fourthCheck = parentFence?.isCreated && childFence?.isCreated;
    const fifthCheck =
      parentFence?.isCreated &&
      parentFence?.childFence?.isPointInside &&
      this.hasFenceWithOutSidePoint();
    return (
      firstCheck ||
      secondCheck ||
      thirdCheck ||
      fourthCheck ||
      fifthCheck ||
      isFenceSelfIntersecting
    );
  }

  private applyOutsideBlueOverlay(polygonLayer: L.Polygon) {
    polygonLayer.setStyle({ fillColor: 'transparent', fillOpacity: 0 });

    // Create an "outer" polygon that covers a larger area
    const outerCoords: [number, number][] = [
      [-90, -180],
      [-90, 180],
      [90, 180],
      [90, -180],
      [-90, -180],
    ];

    const outsideOverlay = L.polygon([outerCoords as any, polygonLayer.getLatLngs()[0]], {
      fillColor: '#1a79ff',
      fillOpacity: 0.2,
      pane: 'virtualFencePane',
      stroke: false,
    });

    this.mapService.layers.polygons.addLayer(outsideOverlay);
    (outsideOverlay as any).overlayId = `overlay-${(polygonLayer as any).customId}`;
    outsideOverlay.bringToBack();
  }

  private removeBlueOverlayLayer(insidePoint: { lat: number; lng: number }) {
    const overlayLayer = this.mapService.layers.polygons
      .getLayers()
      .find((layer: any) => layer.overlayId == `overlay-${insidePoint.lat}|${insidePoint.lng}`);
    if (overlayLayer) {
      this.mapService.layers.polygons.removeLayer(overlayLayer);
    }
  }

  cancelNewPolygonCreation() {
    this.resetPolygonHandlers();
    this.removeActiveLayer();
  }

  removeActiveLayer() {
    if (this.currentDrawnLayer()) {
      this.mapService.layers.polygons.removeLayer(this.currentDrawnLayer());
      this.currentDrawnLayer.set(null);
    }
  }

  private resetPolygonHandlers() {
    this.drawHandler()?.disable();
    this.drawHandler.set(null);

    this.isEditMode.set(false);
    this.currentlyEditingFenceId = null;
    // reset for new layer to handle cancel
    if (this.drawingCanvasLayer) {
      this.map?.removeLayer(this.drawingCanvasLayer);
      this.drawingCanvasLayer = null;
    }
    this.currentDrawnLayer.set(null);
    this.drawingManagerService.setActiveDrawingMode(null);
    this.map?.off(L.Draw.Event.CREATED, undefined, this);
  }

  startEditingPolygon(e: { lat: number; lng: number; currentPointType: boolean }) {
    this.isEditMode.set(true);
    this.drawingManagerService.setActiveDrawingMode('virtual-fence');

    this.currentPointTypeEditMode = e.currentPointType;
    this.currentlyEditingFenceId = `${e.lat}|${e.lng}`;

    this.mapService.layers.polygons.eachLayer((layer: L.Layer) => {
      if (layer instanceof L.Polygon && (layer as any).customId == this.currentlyEditingFenceId) {
        (layer as any).editing.enable();
      }
    });
  }

  saveEditedPolygon(currentPoint: { lat: number; lng: number }) {
    const uniqueId = `${currentPoint.lat}|${currentPoint.lng}`;

    this.mapService.layers.polygons.eachLayer((layer: L.Layer) => {
      if (layer instanceof L.Polygon && (layer as any).customId == uniqueId) {
        const updatedLatLngs = layer.getLatLngs()[0] as L.LatLng[];
        const point = this.getInsideOutsidePoint(this.currentPointTypeEditMode, updatedLatLngs);
        const isValid = this.isPointValid(point, updatedLatLngs);
        if (point && isValid === this.currentPointTypeEditMode) {
          this.createPolygon(layer, point, updatedLatLngs, true);
        } else {
          alert('Something went wrong. Please try again.');
        }
      }
    });
    this.drawingManagerService.setActiveDrawingMode(null);
  }

  deletePolygon(insidePoint: { lat: number; lng: number }) {
    const polygonLayer = this.mapService.layers.polygons
      .getLayers()
      .find((layer: any) => layer.customId == `${insidePoint.lat}|${insidePoint.lng}`);

    if (polygonLayer) {
      this.mapService.layers.polygons.removeLayer(polygonLayer);
    }

    // remove blueoverly if exists
    this.removeBlueOverlayLayer(insidePoint);

    const updatedPolygons = this.allPolygons().filter(
      (fence: Polygon) =>
        !(fence.insidePoint.lat === insidePoint.lat && fence.insidePoint.lng === insidePoint.lng),
    );

    this.allPolygons.set(updatedPolygons);
    if (!updatedPolygons.length) this.isEmptyVirtualFence.set(true);
  }

  setFocusOnPolygon(insidePoint: { lat: number; lng: number }) {
    const polygonData: { fencePoints: { lat: number; lng: number }[] } | undefined =
      this.allPolygons().find(
        (fence) =>
          fence.insidePoint.lat === insidePoint.lat && fence.insidePoint.lng === insidePoint.lng,
      );

    if (polygonData && this.map) {
      const layer = L.polygon(polygonData.fencePoints.map(({ lat, lng }) => [lat, lng])).addTo(
        this.map,
      );
      this.map.fitBounds(layer.getBounds(), {
        maxZoom: 20,
        padding: [80, 80],
      });
      setTimeout(() => this.map.removeLayer(layer), 300);
    } else {
      this.snackbarService.error('Polygon with specified LatLng not found.');
    }
  }

  private hideCircularMarker() {
    const circleMarkerButton = document.querySelector(
      '.leaflet-draw-draw-circlemarker',
    ) as HTMLElement;
    if (circleMarkerButton) circleMarkerButton.style.display = 'none';
  }

  private setBounds() {
    const allPolygonsBounds = this.mapService.layers.polygons.getBounds();

    if (allPolygonsBounds.isValid()) {
      setTimeout(() => {
        const size = this.map?.getSize() ?? { x: 0, y: 0 };
        const minPadding = 20; // Ensure at least 20px padding
        const padX = Math.max(minPadding, 0.1 * size.x); // 10% of width, minimum 20px
        const padY = Math.max(minPadding, 0.1 * size.y); // 10% of height, minimum 20px

        this.map?.fitBounds(allPolygonsBounds, {
          maxZoom: 12,
          padding: [padX, padY],
        });
      }, 200);
    }
  }

  private hasPartialOverlap(newFencePolygon: any) {
    if (this.allPolygons().length === 0) return false;

    return this.allPolygons().some((fence) => {
      const existingFenceCoords = fence.fencePoints.map((point) => [point.lng, point.lat]);
      const closedExistingFenceCoords = existingFenceCoords.concat([existingFenceCoords[0]]);
      const existingFencePolygon = polygon([closedExistingFenceCoords]);

      const isIntersecting = booleanIntersects(newFencePolygon, existingFencePolygon);
      const isNotContained = !booleanContains(existingFencePolygon, newFencePolygon);
      // const isWithin = !turf.booleanWithin(existingFencePolygon, newFencePolygon);
      return isIntersecting && isNotContained;
    });
  }

  private adjacentFenceCreated(newFencePolygon: any) {
    if (this.allPolygons().length === 0) return null;
    for (const fence of this.allPolygons()) {
      const existingFenceCoords = fence.fencePoints.map(({ lng, lat }) => [lng, lat]);
      const closedExistingFenceCoords = existingFenceCoords.concat([existingFenceCoords[0]]);
      const existingFencePolygon = polygon([closedExistingFenceCoords]);

      const isDisjoint = booleanDisjoint(newFencePolygon, existingFencePolygon);
      if (isDisjoint) return isDisjoint;
    }
    return false;
  }

  private parentFenceCreated(newFencePolygon: any): { isCreated: boolean; childFence: Polygon } {
    if (this.allPolygons().length === 0) return null;

    //skip currently editing fence
    for (const fence of this.allPolygons()) {
      if (this.currentlyEditingFenceId === `${fence.insidePoint.lat}|${fence.insidePoint.lng}`) {
        continue;
      }
      const existingFenceCoords = fence.fencePoints.map((point) => [point.lng, point.lat]);
      const closedExistingFenceCoords = existingFenceCoords.concat([existingFenceCoords[0]]);
      const existingFencePolygon = polygon([closedExistingFenceCoords]);

      const isChildFence = booleanContains(newFencePolygon, existingFencePolygon);
      if (isChildFence) return { isCreated: isChildFence, childFence: fence };
    }

    return { isCreated: false, childFence: null };
  }

  private childFenceCreated(newFencePolygon: any): { isCreated: boolean; parentFence: Polygon } {
    const allPolygons = this.allPolygons(); // Cache the polygons to avoid repeated calls
    if (allPolygons.length === 0) return null;

    let immediateParent: any = null;
    let smallestArea = Infinity;

    for (const fence of allPolygons) {
      if (this.currentlyEditingFenceId === `${fence.insidePoint.lat}|${fence.insidePoint.lng}`) {
        continue;
      }

      const existingFenceCoords = fence.fencePoints.map((point) => [point.lng, point.lat]);
      const closedExistingFenceCoords = existingFenceCoords.concat([existingFenceCoords[0]]);
      const existingFencePolygon = polygon([closedExistingFenceCoords]);

      if (booleanWithin(newFencePolygon, existingFencePolygon)) {
        const areaOfFencePolygon = area(existingFencePolygon);

        if (areaOfFencePolygon < smallestArea) {
          smallestArea = areaOfFencePolygon;
          immediateParent = fence;
        }
      }
    }
    if (immediateParent) return { isCreated: true, parentFence: immediateParent };

    return { isCreated: false, parentFence: null };
  }

  private doesNewFenceContainAllFences(newFencePolygon: any) {
    if (this.allPolygons().length === 0) return false;
    for (const fence of this.allPolygons()) {
      // Skip currently editing fence if necessary
      if (this.currentlyEditingFenceId === `${fence.insidePoint.lat}|${fence.insidePoint.lng}`) {
        continue;
      }
      const existingFenceCoords = fence.fencePoints.map(({ lat, lng }) => [lng, lat]);
      const closedExistingFenceCoords = existingFenceCoords.concat([existingFenceCoords[0]]);
      const existingFencePolygon = polygon([closedExistingFenceCoords]);

      const isChildFence = booleanWithin(existingFencePolygon, newFencePolygon);
      if (!isChildFence) return false;
    }
    return true;
  }

  private hasFenceWithOutSidePoint(excludeFenceId = '') {
    return this.allPolygons().some(({ insidePoint, isPointInside }) => {
      const fenceId = `${insidePoint.lat}|${insidePoint.lng}`;
      return !isPointInside && fenceId !== excludeFenceId;
    });
  }

  dataAsPolygonStructure(allPolygons: dsDtos.Command[] | dsDtos.Collar[]): Polygon[] {
    const insidePointCount = new Map<string, number>();

    allPolygons.forEach((command: dsDtos.Collar | dsDtos.Command) => {
      command.virtualFenceConstructionRules?.forEach(({ insidePoint }) => {
        const key = `${insidePoint.latitude},${insidePoint.longitude}`;
        insidePointCount.set(key, (insidePointCount.get(key) || 0) + 1);
      });
    });

    const commonInsidePoints = new Set<string>();
    const totalDevices = allPolygons.length;

    insidePointCount.forEach((count, key) => {
      if (count === totalDevices) commonInsidePoints.add(key);
    });

    const uniqueCommonFences = new Map<string, Polygon>();

    allPolygons.forEach((command: dsDtos.Collar | dsDtos.Command) => {
      command.virtualFenceConstructionRules?.forEach(({ insidePoint, fencePoints }, index) => {
        const key = `${insidePoint.latitude},${insidePoint.longitude}`;
        if (commonInsidePoints.has(key) && !uniqueCommonFences.has(key)) {
          uniqueCommonFences.set(key, {
            isPointInside: true,
            insidePoint: {
              lat: insidePoint.latitude,
              lng: insidePoint.longitude,
            },
            fencePoints: Array.from(
              new Map<string, { lat: number; lng: number }>(
                fencePoints.map(({ latitude, longitude }) => [
                  `${latitude},${longitude}`,
                  { lat: latitude, lng: longitude },
                ]),
              ).values(),
            ),
            name: 'Fence ' + (index + 1),
          });
        }
      });
    });

    return Array.from(uniqueCommonFences.values());
  }

  displayFetchedPolygons(allPolygons: Polygon[], shouldSetBounds = true) {
    allPolygons.forEach((fence) => {
      const insidePoint = point([fence.insidePoint.lng, fence.insidePoint.lat]);
      const polygonPoints = fence.fencePoints.map(({ lng, lat }) => [lng, lat]);
      if (
        polygonPoints.length > 0 &&
        (polygonPoints[0][0] !== polygonPoints[polygonPoints.length - 1][0] ||
          polygonPoints[0][1] !== polygonPoints[polygonPoints.length - 1][1])
      ) {
        polygonPoints.push(polygonPoints[0]);
      }

      const poly = polygon([polygonPoints]);

      const isChoosenPointInside = booleanPointInPolygon(insidePoint, poly);

      const latlngs = fence.fencePoints.map(({ lng, lat }) => L.latLng(lat, lng));
      const polygonLayer = L.polygon(latlngs);
      // need to set before calling applyOutsideBlueOverlay
      (polygonLayer as any).customId = `${fence.insidePoint.lat}|${fence.insidePoint.lng}`;
      fence.isPointInside = true;

      if (!isChoosenPointInside) {
        fence.isPointInside = false;
        this.applyOutsideBlueOverlay(polygonLayer);
      }

      this.displayFencePopup(polygonLayer, fence.name);
      this.mapService.layers.polygons.addLayer(polygonLayer);
    });
    const map = this.map;
    if (!map) return;

    const runAdd = () => {
      map.invalidateSize({ animate: false });
      map.addLayer(this.mapService.layers.polygons);
      if (shouldSetBounds) {
        this.setBounds();
      }
    };

    const container = map.getContainer();
    if (container?.clientWidth > 0 && container?.clientHeight > 0) {
      runAdd();
    } else if (container) {
      const observer = new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        if (rect && rect.width > 0 && rect.height > 0) {
          observer.disconnect();
          runAdd();
        }
      });
      observer.observe(container);
    }
  }

  prepareVirtualFenceRules(fences: Polygon[]): dsDtos.VirtualFenceConstructionRule[] {
    if (fences.length === 0) return [];

    return fences.map(({ insidePoint, fencePoints }) => ({
      insidePoint: {
        latitude: insidePoint.lat,
        longitude: insidePoint.lng,
      },
      fencePoints: fencePoints.map(({ lat, lng }) => ({
        latitude: lat,
        longitude: lng,
      })),
    }));
  }

  private isPointValid(insidePoint: Point, polygonLatLngs: L.LatLng[]) {
    const chosenPoint = point([insidePoint.latlng.lng, insidePoint.latlng.lat]);
    const polygonPoints = polygonLatLngs.map(({ lng, lat }) => [lng, lat]);
    if (
      polygonPoints.length > 0 &&
      (polygonPoints[0][0] !== polygonPoints[polygonPoints.length - 1][0] ||
        polygonPoints[0][1] !== polygonPoints[polygonPoints.length - 1][1])
    ) {
      polygonPoints.push(polygonPoints[0]);
    }

    const poly = polygon([polygonPoints]);

    return booleanPointInPolygon(chosenPoint, poly);
  }

  getFenceRules(polygonLatLngs: L.LatLng[]): FenceRules {
    const newFenceCoords = polygonLatLngs.map(({ lng, lat }) => [lng, lat]);
    const newFencePolygon = polygon([newFenceCoords.concat([newFenceCoords[0]])]);

    const selfIntersections = kinks(newFencePolygon);
    const isFenceSelfIntersecting = selfIntersections.features.length > 0;

    const parentFence = this.parentFenceCreated(newFencePolygon);
    const childFence = this.childFenceCreated(newFencePolygon);
    const isPartialFence = this.hasPartialOverlap(newFencePolygon);
    const isAdjacentFence = this.adjacentFenceCreated(newFencePolygon);
    const doesContainAllFences = this.doesNewFenceContainAllFences(newFencePolygon);
    return {
      parentFence,
      childFence,
      isPartialFence,
      isAdjacentFence,
      doesContainAllFences,
      isFenceSelfIntersecting,
    };
  }

  markRedAndRemove(layer: L.Polygon) {
    layer.setStyle({ color: 'red', weight: 2, opacity: 1 });
    setTimeout(() => this.mapService.layers.polygons.removeLayer(layer), 2000);
  }

  virtualFencePointChanged(e: { lat: number; lng: number; event: MatButtonToggleChange }) {
    const { lat, lng, event } = e;
    this.currentPointTypeEditMode = event.value;
    const uniqueId = `${lat}|${lng}`;

    this.mapService.layers.polygons.eachLayer((layer: L.Layer) => {
      if (layer instanceof L.Polygon && (layer as any).customId == uniqueId) {
        const updatedLatLngs = layer.getLatLngs()[0] as L.LatLng[];
        const point = this.getInsideOutsidePoint(this.currentPointTypeEditMode, updatedLatLngs);
        const isValid = this.isPointValid(point, updatedLatLngs);
        if (point && isValid === this.currentPointTypeEditMode) {
          const rules = this.getFenceRules(updatedLatLngs);
          const isValid = this.checkConditions(rules, point, true, uniqueId);
          if (isValid) {
            this.removeBlueOverlayLayer({ lat, lng });
            this.applyColorToMap(point, layer);
          }
        }
      }
    });
  }

  private displayFencePopup(layer: L.Polygon, name: string): void {
    layer.bindPopup(`<b>${name}</b>`).openPopup();
    setTimeout(() => layer.closePopup(), 4000);
  }

  onSetEmptyVirtualFence() {
    this.clearAllPolygons();
    this.allPolygons.set([]);
    this.isEmptyVirtualFence.set(true);
  }

  onVirtualFenceFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file && file.name.toLowerCase().endsWith('.kml')) {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const kmlData = e.target?.result as string;

        // Parse KML for rendering and metadata
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(kmlData, 'text/xml');
        const geoJsonObject = kml(kmlDoc);
        const kmlLayer = L.geoJSON(geoJsonObject).addTo(this.map);
        const placemarks = kmlDoc.getElementsByTagName('Placemark');
        const nameMap: Map<string, string | null> = new Map();

        for (let i = 0; i < placemarks.length; i++) {
          const placemark = placemarks[i];
          const nameElement = placemark.getElementsByTagName('name')[0];
          const name = nameElement ? nameElement.textContent || null : null; // Set to null if name is missing
          nameMap.set(`Fence-${i}`, name);
        }

        // Associate names with polygons
        kmlLayer.getLayers().forEach((layer: L.Layer, index: number) => {
          if (layer instanceof L.Polygon) {
            const latLngs = layer.getLatLngs()[0] as L.LatLng[];

            const polygonLatLngs = latLngs.map(({ lat, lng }) => new L.LatLng(lat, lng));
            this.mapService.layers.polygons.addLayer(layer);

            const fenceName = nameMap.get(`Fence-${index}`) || null; // Use null if name is not found

            const rules = this.getFenceRules(polygonLatLngs);
            const isNotValid = this.checkConditionsNewPolygon(rules);

            if (isNotValid) {
              this.markRedAndRemove(layer);
              this.snackbarService.error(`${fenceName} creation not possible.`);
            } else {
              this.beforeNewPolygonCreation(layer, polygonLatLngs, fenceName);
              this.map?.fitBounds(kmlLayer.getBounds());
            }
          }
        });
      };
      reader.readAsText(file);
      input.value = '';
    } else {
      alert('Please select a valid .kml file.');
    }
  }

  updateAndShowPolygons(polygons: Polygon[], isInit?: boolean) {
    if (isInit) this.initialPolygons.set(polygons);
    this.allPolygons.set(polygons);
    if (this.map) {
      this.displayFetchedPolygons(polygons, false);
    }
  }

  resetVirtualFence() {
    if (!this.isEmptyVirtualFence()) {
      this.allPolygons()?.forEach(({ insidePoint }) => this.deletePolygon(insidePoint));
    }
    this.isEmptyVirtualFence.set(false);
  }

  clearAllPolygons() {
    this.mapService.layers.polygons.clearLayers();
  }

  resetToDefaultVirtualFence() {
    this.resetVirtualFence();
    this.allPolygons.set(cloneDeep(this.initialPolygons()));
    this.displayFetchedPolygons(this.allPolygons(), true);
    this.isEditMode.set(false);
  }
}
